import { fetchHelperHealth, resolveHelperConnectionState, uploadViaHelper } from '@/features/serial/helperClient';

export async function isHelperAvailable(): Promise<boolean> {
  if (import.meta.env.DEV) {
    return true;
  }
  const health = await fetchHelperHealth();
  return resolveHelperConnectionState(health) === 'ready';
}

export async function getHelperConnectionState() {
  const health = await fetchHelperHealth();
  return resolveHelperConnectionState(health);
}

export interface UploadSketchParams {
  code: string;
  fqbn: string;
  port: string;
  /** Libraries that still need installing before compile (empty when already installed). */
  librariesToInstall?: string[];
  onProgress?: (progress: number, status: string) => void;
}

export interface UploadResult {
  log: string;
  source: 'helper' | 'dev';
}

interface DevUploadStartResponse {
  uploadId?: string;
  error?: string;
}

interface DevUploadProgressResponse {
  progress: number;
  status: string;
  state: 'running' | 'complete' | 'error';
  log?: string;
  error?: string;
}

const DEV_UPLOAD_TIMEOUT_MS = 180_000;
const PROGRESS_POLL_MS = 200;
const LIB_INSTALL_PHASE_MS = 12_000;
const COMPILE_PHASE_MS = 8_000;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function startUploadPhaseTimer(
  librariesToInstall: string[],
  onProgress?: (progress: number, status: string) => void,
): () => void {
  if (!onProgress) {
    return () => undefined;
  }

  const hasLibraries = librariesToInstall.length > 0;
  let phase = hasLibraries ? 0 : 1;
  let elapsedMs = 0;

  const tick = () => {
    elapsedMs += 1000;

    if (hasLibraries && phase === 0 && elapsedMs >= LIB_INSTALL_PHASE_MS) {
      phase = 1;
      onProgress(42, 'Compiling…');
      return;
    }

    const compileElapsed = hasLibraries ? elapsedMs - LIB_INSTALL_PHASE_MS : elapsedMs;
    if (phase === 1 && compileElapsed >= COMPILE_PHASE_MS) {
      phase = 2;
      onProgress(68, 'Uploading to board…');
    }
  };

  if (hasLibraries) {
    onProgress(24, 'Installing required libraries…');
  } else {
    onProgress(28, 'Compiling…');
  }

  const intervalId = window.setInterval(tick, 1000);
  return () => window.clearInterval(intervalId);
}

async function pollUploadProgress(
  uploadId: string,
  onProgress?: (progress: number, status: string) => void,
): Promise<string> {
  const startedAt = Date.now();

  while (Date.now() - startedAt < DEV_UPLOAD_TIMEOUT_MS) {
    const response = await fetch(`/api/dev/upload/progress/${encodeURIComponent(uploadId)}`, {
      signal: AbortSignal.timeout(DEV_UPLOAD_TIMEOUT_MS),
    });

    if (!response.ok) {
      throw new Error('Upload progress could not be read.');
    }

    const job = (await response.json()) as DevUploadProgressResponse;
    onProgress?.(job.progress, job.status);

    if (job.state === 'complete') {
      onProgress?.(100, 'Upload complete');
      return job.log ?? 'Upload complete.';
    }

    if (job.state === 'error') {
      throw new Error(job.error ?? 'Upload failed.');
    }

    await sleep(PROGRESS_POLL_MS);
  }

  throw new Error('Upload timed out.');
}

async function uploadViaDevServer(params: UploadSketchParams): Promise<UploadResult> {
  const startResponse = await fetch('/api/dev/upload', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      code: params.code,
      fqbn: params.fqbn,
      port: params.port,
    }),
    signal: AbortSignal.timeout(DEV_UPLOAD_TIMEOUT_MS),
  });

  const startData = (await startResponse.json()) as DevUploadStartResponse;

  if (!startResponse.ok || !startData.uploadId) {
    throw new Error(startData.error ?? 'Upload failed.');
  }

  params.onProgress?.(12, 'Compiling…');

  const log = await pollUploadProgress(startData.uploadId, params.onProgress);

  return {
    log,
    source: 'dev',
  };
}

export async function uploadSketch(params: UploadSketchParams): Promise<UploadResult> {
  const librariesToInstall = params.librariesToInstall ?? [];
  const health = await fetchHelperHealth();
  if (health?.status === 'ok' && health.token) {
    try {
      const stopPhaseTimer = startUploadPhaseTimer(librariesToInstall, params.onProgress);
      try {
        const result = await uploadViaHelper(health.token, params);
        params.onProgress?.(100, 'Upload complete');
        return { ...result, source: 'helper' };
      } finally {
        stopPhaseTimer();
      }
    } catch (error) {
      if (!import.meta.env.DEV) {
        throw error;
      }
    }
  }

  if (import.meta.env.DEV) {
    if (librariesToInstall.length > 0) {
      params.onProgress?.(18, 'Installing required libraries…');
    }
    return uploadViaDevServer(params);
  }

  throw new Error(
    'Skyrover helper is not running. Start the helper app to compile and upload sketches.',
  );
}
